/**
 * Branded integer type representing an amount in cents (AUD).
 *
 * All monetary values in this system are stored as integer cents.
 * Floating-point money is banned throughout pipeline and contract source.
 *
 * Implementation note: `Cents` is the inferred output type of `zCents`.
 * Using Zod's built-in brand ensures the type is consistent across all
 * Zod-inferred schema types and hand-typed entities.
 */

import { z } from 'zod';

/**
 * Zod schema for the Cents branded type.
 * Accepts any safe integer; rejects floats, NaN, and Infinity.
 */
export const zCents = z
  .number()
  .int('Monetary values must be integer cents, not fractional')
  .safe('Monetary value exceeds safe integer range')
  .brand<'Cents'>();

/**
 * The Cents branded type — inferred from zCents for full consistency with
 * all Zod-parsed entity types.
 */
export type Cents = z.infer<typeof zCents>;

/**
 * Constructs a Cents value from a plain integer.
 * Throws if `n` is not a finite, safe integer.
 */
export function cents(n: number): Cents {
  if (!Number.isFinite(n)) {
    throw new TypeError(`cents(): value must be finite, got ${n}`);
  }
  if (!Number.isInteger(n)) {
    throw new TypeError(
      `cents(): value must be an integer (no fractional cents), got ${n}`,
    );
  }
  if (!Number.isSafeInteger(n)) {
    throw new TypeError(
      `cents(): value exceeds safe integer range, got ${n}`,
    );
  }
  return n as Cents;
}
